import { JSX, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import CameraActionbar from "./CameraActionbar";
import PictureActionbar from "./PictureActionbar";

export type CameraViewProps = {
	onCapture: (value: Blob) => void;
};

function PictureCaptureView(props: CameraViewProps): JSX.Element {
	const PREVIEW_WIDTH = 200;
	const PREVIEW_HEIGHT = 200;

	const ANIMATION_DURATION = 600;

	const { t } = useTranslation();

	const [camera, setCamera] = useState(false);
	const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
	const [videoInput, setVideoInput] = useState(0);
	const [error, setError] = useState<string>();
	const [processing, setProcessing] = useState(false);

	const playerRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		navigator.mediaDevices
			?.enumerateDevices()
			.then(devices =>
				devices.filter(device => device.kind === "videoinput")
			)
			.then(setVideoInputs);
	}, [navigator.mediaDevices]);

	useEffect(() => {
		if (camera) {
			startStream();
		}
		return () => stopStream();
	}, [camera, videoInput, videoInputs]);

	function stopStream(): void {
		const stream = playerRef.current?.srcObject as MediaStream;
		if (stream) {
			stream.getVideoTracks().forEach(track => track.stop());
		}
	}

	function startStream() {
		setError(undefined);

		if (videoInput >= videoInputs.length) {
			throw new Error("video input index out of range");
		}

		navigator.mediaDevices
			.getUserMedia({
				video: {
					deviceId: videoInputs[videoInput].deviceId
				}
			})
			.then(stream => {
				const video = playerRef.current;
				if (video !== null) {
					video.srcObject = stream;
					video.play();
				}
			})
			.catch(() => {
				setError(t("cameraFailure"));
				stopStream();
			});
	}

	function showAnimation(): void {
		setProcessing(true);
		const video = playerRef.current;

		video?.pause();
		setTimeout(() => {
			video?.play();
			setProcessing(false);
		}, ANIMATION_DURATION);
	}

	const capture = useCallback((): void => {
		setError(undefined);

		const player = playerRef.current;
		if (player === undefined || player === null) {
			setError(t("cameraFailure"));
			return;
		}

		const stream = player.srcObject as MediaStream;
		if (!stream.active) {
			setError(t("cameraFailure"));
			return;
		}

		showAnimation();

		const canvas: HTMLCanvasElement = document.createElement("canvas");
		canvas.width = player.videoWidth;
		canvas.height = player.videoHeight;

		const context = canvas.getContext("2d");
		if (context === null) {
			return;
		}
		context.drawImage(player, 0, 0, canvas.width, canvas.height);

		canvas.toBlob(blob => {
			if (blob === null) {
				return;
			}

			props.onCapture(blob);
		});
	}, [playerRef, props.onCapture]);

	const nextCamera = useCallback(() => {
		setError(undefined);
		setVideoInput(previous => (previous + 1) % videoInputs.length);
	}, [videoInputs]);

	const openCamera = useCallback((): void => {
		setError(undefined);
		setCamera(true);
	}, []);

	const closeCamera = useCallback((): void => {
		setError(undefined);
		stopStream();
		setCamera(false);
	}, [playerRef]);

	return (
		<Grid container direction="column">
			{camera ? (
				<CameraActionbar
					videoInputs={videoInputs.length}
					disabled={processing}
					onCaptureImage={capture}
					onToggleCamera={nextCamera}
					onCloseCamera={closeCamera}
				/>
			) : (
				<PictureActionbar
					videoInputs={videoInputs.length}
					onUploadImage={props.onCapture}
					onOpenCamera={openCamera}
				/>
			)}

			{error && (
				<Grid item>
					<Typography
						aria-label="error"
						variant="caption"
						color="error">
						{error}
					</Typography>
				</Grid>
			)}

			{camera && (
				<Grid item>
					<div
						style={{
							display: "grid",
							maxWidth: PREVIEW_WIDTH,
							maxHeight: PREVIEW_HEIGHT
						}}>
						<video
							aria-label="video"
							ref={playerRef}
							autoPlay
							style={{
								maxWidth: "100%",
								maxHeight: "100%"
							}}
						/>

						<div
							style={{
								position: "absolute",
								width: PREVIEW_WIDTH,
								height: PREVIEW_HEIGHT,
								backgroundColor: processing
									? "rgba(255, 255, 255, 0.7)"
									: "rgba(255, 255, 255, 0)",
								transitionDuration: ANIMATION_DURATION + "ms"
							}}
						/>
					</div>
				</Grid>
			)}
		</Grid>
	);
}

export default PictureCaptureView;
